import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { AddressComponent } from '../../../../shared/components/address/address.component';
import { ListTableComponent } from '@ppl/ui';
import { BaseComponent } from '../../../../shared/utils/base.component';
import { SectionsService } from '../../../../services/sections.service';
import { LeadDetailsService } from '../../../../services/lead-details.service';
import {
  STATIC_DATA,
  STATIC_JSON_DATA_PATH,
} from '../../../../shared/constants/static-data.constants';
import { performValidation } from '../../../../shared/utils/validation.util';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { CommonModule } from '@angular/common';
import { LoggerService } from '../../../../shared/services/logger.service';
import {
  LeadDetailIdentifiers,
  LeadDetailIdentifiersAddUpdateModel,
  LeadDetailIdentifiersModel,
} from '../../../../interfaces/lead-detail.model';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { AuthorizeDirective } from '../../../../shared/authorization/authorize.directive.';
import { ButtonComponent } from '@ppl/ui';
import { LookupValueItem } from '../../../../interfaces/lookup.interface';
import { NzAriaLabelDirective } from '../../../../shared/directives/nz-aria-label.directive';
import { AuthorizationUtility } from '../../../../shared/authorization/auth.utility';
import { NotificationService } from '../../../../shared/services/notification.service';
import { UserService } from '../../../../services/user.service';
import { DropdownSelectComponent } from '@ppl/ui';
import {
  isDuplicateIdentifierError,
  isDuplicateIdentifierPayload,
} from '../../../../shared/utils/duplicate-identifier.util';

@Component({
  selector: 'ppl-lead-detail-identifiers',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgZorroModule,
    ReactiveFormsModule,
    AddressComponent,
    ListTableComponent,
    AuthorizeDirective,
    ButtonComponent,
    NzAriaLabelDirective,
    DropdownSelectComponent,
    NzAlertModule,
  ],
  templateUrl: './lead-detail-identifiers.component.html',
  styleUrl: './lead-detail-identifiers.component.css',
})
export class LeadDetailIdentifiersComponent
  extends BaseComponent
  implements OnInit, OnChanges {
  mode = 'read';
  identifierForm!: FormGroup;
  filledInfo!: any;
  cols: any;
  gridData: any[] = [];
  identifiersList: LookupValueItem[] = [];

  // Per-enrollment-type identifier allowlist for provider entries. Filtering is a UNION (OR)
  // across all enrollment types on the lead — if any type allows an identifier, it's allowed.
  private readonly ALLOWED_IDENTIFIERS_BY_ENROLLMENT_TYPE: Record<string, string[]> = {
    employer:               ['SSN', 'ITIN', 'EIN', 'EINES'],
    vendor:                 ['SSN', 'EIN'],
    contractor:             ['SSN', 'EIN'],
    independentFacilitator: ['SSN', 'EIN'],
    provider:               ['SSN'],
    // legalGuardian / conservator / poa: contribute no identifiers
  };

  /** Build the union of allowed identifier codes for the given enrollment types. */
  private getAllowedIdentifierCodes(enrollmentTypes: string[]): Set<string> {
    const codes = new Set<string>();
    for (const t of enrollmentTypes) {
      (this.ALLOWED_IDENTIFIERS_BY_ENROLLMENT_TYPE[t] ?? []).forEach(c => codes.add(c));
    }
    return codes;
  }
  copyOfGridData: any[] = [];
  steps!: any;
  entry = 'base';
  selectedLead: any;
  actionButtons: TableRowActionsModel = new TableRowActions();
  createdIdentifiers: string[] = [];
  approvedStatus: string = STATIC_DATA.APPROVED;
  leadStatus = '';
  selectedIdentifierValue: string | undefined | null = null;
  ssnVisible = false;
  itinVisible = false;
  einesVisible = false;
  deleteErrorMessage = '';
  // Set when the backend rejects the save because the identifier is already on another
  // record (HTTP 406 / enr_101). Shown inline so the user can correct the value in place.
  duplicateIdentifierError = '';
  ssnError = false;
  cinError = false;
  einError = false;
  einesError = false;
  tinError = false;
  itinError = false;
  isSectionEditable: boolean = false;
  isSectionDeletable: boolean = false;
  infoData: any;
  canUnMask: boolean = false;
  canEditSSN: boolean = false;
  isIdentifierTypeEditable: boolean = false;
  requireIdentifiers: boolean = true;
  // Gates the Add/Save edit flow. True when the user has section edit permission,
  // OR the special case: a provider (external user) viewing their own profile — who
  // is read-only overall but must still be able to submit their own identifiers.
  canAddIdentifiers: boolean = false;

  get showSsnItinInfo(): boolean {
    const isReferralOrParticipant = this.entry === STATIC_DATA.REFERRAL || this.entry === STATIC_DATA.PARTICIPANT_ENROLLMENT;
    const normalizedProviderType = String(this.infoData?.providerType ?? '').trim().toLowerCase();
    const enrollmentTypes: string[] = this.sectionService.getEnrollmentType() ?? [];
    const isEmployerProviderType = normalizedProviderType === 'emp' || enrollmentTypes.includes('employer');
    if (!isReferralOrParticipant && !isEmployerProviderType) return false;
    const hasSsnOrItin = this.gridData?.some(identifier => identifier?.type === 'SSN' || identifier?.type === 'ITIN');
    if (!hasSsnOrItin) return false;
    if (this.mode === 'add') return true;
    if (this.mode === 'edit' && this.isIdentifierTypeEditable) return true;
    return false;
  }
  editingIdentifierType: string | null = null;

  get ssnItinOptions(): LookupValueItem[] {
    return this.identifiersList
      .filter(item => ['SSN', 'ITIN'].includes(item.lkpValueCode))
      .map(item => ({
        ...item,
        disabled: item.lkpValueCode !== this.editingIdentifierType && this.createdIdentifiers.includes(item.lkpValueCode)
      }));
  }

  @Input() pageId = '';
  @Input() tabId = '';
  @Input() programCode = '';
  @Input() readOnly = false;
  @Input() identifier = '';
  @Input() type = '';
  @Input() category = '';
  @Input() set sectionData(data: string) {
    this.steps = data;
    /* console.log(this.steps); */
  }
  @Output() leadUpdated = new EventEmitter<boolean>();

  constructor(
    private fb: FormBuilder,
    private sectionService: SectionsService,
    private leadDetailsService: LeadDetailsService,
    private logger: LoggerService,
    private authUtility: AuthorizationUtility,
    private notificationService: NotificationService,
    private userService: UserService
  ) {
    super();
  }

  async ngOnInit() {
    this.isSectionEditable = this.readOnly ? false : this.authUtility.isSectionEditable(
      this.pageId,
      this.tabId,
      'Identifiers'
    );
    this.isSectionDeletable = this.readOnly ? false : this.authUtility.isSectionDeletable(
      this.pageId,
      this.tabId,
      'Identifiers'
    );
    const userProgram = this.userService.getUserDetails()?.programs.find(pr => pr.program_code === this.programCode);
    const userRoles = userProgram?.roles;
    const isOwnProfile = this.userService.getSelectedRole()?.referenceId === this.userService.getSelectedLead();
    this.canUnMask = isOwnProfile || (userRoles ? userRoles.some(r => r.role_name === 'PPL Admin' || r.role_name === 'BAVAdmin') : false);
    this.canEditSSN = this.canUnMask;
    this.selectedLead = JSON.parse(
      this.userService.getSelectedLead()!
    );
    console.log(this.selectedLead);

    this.entry = this.userService.getLeadEntry() ?? '';
    /* console.log(this.entry); */
    const isProviderEntry =
      this.entry === STATIC_DATA.PROVIDER_ENROLLMENT || this.entry === STATIC_DATA.PROVIDER;
    this.canAddIdentifiers = this.isSectionEditable || (isProviderEntry && isOwnProfile);
    this.createFormGroup();
    const isParticipantEntry = this.entry === STATIC_DATA.PARTICIPANT_ENROLLMENT || this.entry === STATIC_DATA.REFERRAL || this.entry === STATIC_DATA.PARTICIPANT;
    this.identifiersList = this.lookupService.getLookupValue('Identifier_type')
      .filter((item) => item.lkpValueCode !== 'NID' && item.lkpValueCode !== 'MHID');

    await this.getFromData();

    this.gridData = [];
    this.leadDetailsService.getLeadStatus().subscribe((status: string) => {
      this.leadStatus = status;

      if (this.leadStatus === this.approvedStatus) {
        this.actionButtons.edit = false;
        this.actionButtons.delete = false;
        this.actionButtons.unmask = this.canUnMask;
      } else {
        this.actionButtons.edit = this.isSectionEditable;
        this.actionButtons.delete = this.isSectionDeletable;
        this.actionButtons.unmask = this.canUnMask;
      }
    });

    this.leadDetailsService.getUpdatedLeadData().subscribe((data) => {
      this.infoData = data?.responsedata?.infoData;
      this.gridData = data?.responsedata?.infoData?.identifiers?.filter((identifier: any) => identifier?.type !== 'NID' && identifier?.type !== 'MHID');

      const enrollmentTypes: string[] = this.sectionService.getEnrollmentType() ?? [];
      const isEmployerType = enrollmentTypes.includes('employer');
      const isParticipantEntry =
        this.entry === STATIC_DATA.PARTICIPANT_ENROLLMENT ||
        this.entry === STATIC_DATA.REFERRAL ||
        this.entry === STATIC_DATA.PARTICIPANT;
      const isProviderEntry =
        this.entry === STATIC_DATA.PROVIDER_ENROLLMENT ||
        this.entry === STATIC_DATA.PROVIDER;

      // Build the allowed-identifier set as a union across enrollment types (OR semantics).
      // Participant entries always allow SSN/CIN/ITIN regardless of enrollment types.
      const allowedCodes = new Set<string>();
      if (isParticipantEntry) {
        ['SSN', 'CIN', 'ITIN'].forEach(c => allowedCodes.add(c));
      }
      if (isProviderEntry) {
        this.getAllowedIdentifierCodes(enrollmentTypes).forEach(c => allowedCodes.add(c));
      }

      // Strip SSN from gridData only when no enrollment type allows SSN (i.e. POA / Conservator /
      // Legal Guardian exclusively). If any other enrollment type also applies, SSN stays.
      const NO_SSN_TYPES = ['poa', 'conservator', 'legalGuardian'];
      const allTypesBlockSsn =
        enrollmentTypes.length > 0 &&
        enrollmentTypes.every(t => NO_SSN_TYPES.includes(t));
      if (allTypesBlockSsn) {
        this.gridData = this.gridData?.filter((identifier: any) => identifier?.type !== 'SSN');
      }

      if (isProviderEntry) {
        this.requireIdentifiers = !enrollmentTypes.every(type => NO_SSN_TYPES.includes(type));
      }

      // Apply allowlist filter only when we know the entry context — preserves original
      // behavior for unknown / blank entries.
      if (isParticipantEntry || isProviderEntry) {
        this.identifiersList = this.identifiersList.filter(item => allowedCodes.has(item.lkpValueCode));
      }

      if (isEmployerType) {
        this.identifiersList = this.identifiersList.map(item => ({
          ...item,
          lkpValueName: item.lkpValueCode === 'EIN'
            ? 'Employer Identification Number on Record'
            : item.lkpValueCode === 'ITIN'
              ? 'Individual Taxpayer Identification Number ITIN'
              : item.lkpValueCode === 'EINES'
                ? 'Existing Employer Identification Number'
                : item.lkpValueName,
          disabled:
            (item.lkpValueCode === 'ITIN' && this.gridData?.some(identifier => identifier?.type === 'SSN')) ||
            (item.lkpValueCode === 'SSN' && this.gridData?.some(identifier => identifier?.type === 'ITIN'))
        }));
      }

      this.copyAndTransformGridData(this.gridData);
      this.identifiersList = this.identifiersList.map((item) => {
        return {
          ...item,
          disabled: this.shouldDisableIdentifier(item),
        };
      });
    });
  }

  shouldDisableIdentifier = (option: any) => {
    if (this.createdIdentifiers.includes(option.lkpValueCode)) {
      return true;
    }
    const isReferralOrParticipant = this.entry === STATIC_DATA.REFERRAL || this.entry === STATIC_DATA.PARTICIPANT_ENROLLMENT;
    const enrollmentTypes: string[] = this.sectionService.getEnrollmentType() ?? [];
    const isEmployerType = enrollmentTypes.includes('employer');
    const enforcesSsnItinExclusivity = isReferralOrParticipant || isEmployerType;
    if (enforcesSsnItinExclusivity) {
      if (option.lkpValueCode === 'SSN' && this.createdIdentifiers.includes('ITIN')) {
        return true;
      }
      if (option.lkpValueCode === 'ITIN' && this.createdIdentifiers.includes('SSN')) {
        return true;
      }
    }
    return false;
  };

  ngOnChanges(changes: SimpleChanges) {
    try {
      if (changes['sectionData'] && changes['sectionData'].currentValue) {
        this.secData = this.steps['identifiers'];
      }
    } catch (e) {
      this.logger.debug(
        'Error in getting section data and updating latest info',
        e
      );
    }
  }

  createFormGroup() {
    this.identifierForm = this.fb.group({
      leadIdentifierId: [],
      leadId: [],
      type: [],
      value: [],
      /* addlVal1: [],
      addlVal2: [] */
    });
  }

  async getFromData() {
    try {
      if (this.entry) {
        const tempCols = await this.sectionService.getJsonConfigurationData(
          STATIC_JSON_DATA_PATH.LEAD_DETAILS_CONTACT_LIST_HEADERS,
          this.entry
        );
        this.cols = tempCols.identifiers.columns;
      }
    } catch (e) {
      this.logger.debug('Error in getting column data', e);
    }
  }

  copyAndTransformGridData(gridData: any[]) {
    this.createdIdentifiers = [];
    if (gridData) {
      const copyGridData = gridData.slice();

      this.copyOfGridData = copyGridData.map((item) => ({
        ...item,
        identifierCode: item.type,
        type: super.getLookupValue(this.identifiersList, item.type) || (item.type === 'EINES' ? 'Existing Employer Identification Number' : item.type),
        value: item.type === 'EIN' || item.type === 'EINES'
          ? this.maskEINForList(item.value)
          : item.type === 'ITIN'
            ? this.maskITINForList(item.value)
            : item.value,
        modifiedDate: super.formatDateString(item.modifiedDate),
        canEditSSN: this.canEditSSN,
        isEines: item.type === 'EINES'
      }));

      copyGridData.forEach((item) => {
        this.createdIdentifiers.push(item.type);
      });
      if (this.leadStatus !== this.approvedStatus) {
        this.actionButtons.edit = this.isSectionEditable;
        this.actionButtons.delete = this.isSectionDeletable;
        this.actionButtons.unmask = this.canUnMask;
      } else {
        this.actionButtons.edit = false;
        this.actionButtons.delete = false;
        this.actionButtons.unmask = this.canUnMask;
      }
    } else {
      this.copyOfGridData = [];
      this.actionButtons.delete = false;
      this.actionButtons.edit = false;
      this.actionButtons.unmask = this.canUnMask;
    }
  }
  private maskITINForList(value: string): string {
    const raw = value?.replace(/\D/g, '');
    if (!raw || raw.length < 4) return value;
    return `XXX-XX-${raw.slice(-4)}`;
  }

  private maskEINForList(value: string): string {
    const raw = value?.replace(/\D/g, '');
    if (!raw || raw.length < 4) return value;
    return `XX-XXXX${raw.slice(-4)}`;
  }

  onIdentifierTypeChange(): void {
    this.validationErrors = {};
    this.ssnError = false;
    this.cinError = false;
    this.einError = false;
    this.einesError = false;
    this.tinError = false;
    this.itinError = false;
  }

  cancelEdit() {
    this.mode = 'read';
    this.validationErrors = {};
    this.duplicateIdentifierError = '';
    this.identifierForm.reset();
    this.isIdentifierTypeEditable = false;
    this.editingIdentifierType = null;
    this.einesVisible = false;
  }

  action(event: any) {
    console.log('acto', event);

    const selectedId = this.extractId(event.data);
    this.selectedIdentifierValue = this.identifiersList.find(
      (item) => item.lkpValueName === event.data.type
    )?.lkpValueCode;

    //(this.entry === 'referral')? event.data.referralIdentifierId : event.data.leadIdentifierId;
    const selectedData = this.gridData.find((row: any) => {
      if (
        row.leadIdentifierId === selectedId ||
        row.referralIdentifierId === selectedId ||
        row.enrollmentIdentifierId === selectedId ||
        row.providerIdentifierId === selectedId
      ) {
        return row;
      }
    });

    this.deleteErrorMessage = '';
    this.duplicateIdentifierError = '';

    if (event && event.action === 'edit') {
      this.mode = 'edit';
      const isSSNOrITIN = selectedData?.type === 'SSN' || selectedData?.type === 'ITIN';
      const isReferralOrParticipant = this.entry === STATIC_DATA.REFERRAL || this.entry === STATIC_DATA.PARTICIPANT_ENROLLMENT;
      this.isIdentifierTypeEditable = isSSNOrITIN && isReferralOrParticipant;
      this.editingIdentifierType = selectedData?.type || null;

      if (selectedData) {
        console.log('selectedData', selectedData);
        const selectedRowType = selectedData.type;
        if ((selectedRowType === 'SSN' || selectedRowType === 'EIN' || selectedRowType === 'EINES' || selectedRowType === 'TIN' || selectedRowType === 'ITIN') && this.canUnMask) {
          const payload = this.getUnMaskAPIPayload(selectedRowType);

          this.leadDetailsService.unmaskIdentifier(this.programCode, payload).subscribe(data => {
            selectedData.value = data.responsedata;
            this.identifierForm.patchValue(selectedData);
          });
        }
        else {
          this.identifierForm.patchValue(selectedData);
        }

        if (
          this.entry === STATIC_DATA.REFERRAL ||
          this.entry === STATIC_DATA.PARTICIPANT_ENROLLMENT ||
          this.entry === STATIC_DATA.PROVIDER_ENROLLMENT
        ) {
          this.identifierForm.patchValue({
            leadIdentifierId: selectedId,
            leadId: this.selectedLead,
          });
        }
      }
    }

    if (event && event.action === 'delete') {
      console.log('selectedData', selectedData);
      const isReferralOrParticipant = this.entry === STATIC_DATA.REFERRAL || this.entry === STATIC_DATA.PARTICIPANT_ENROLLMENT;
      const isSSNOrITIN = event.data.identifierCode === 'SSN' || event.data.identifierCode === 'ITIN';
      if (isReferralOrParticipant && isSSNOrITIN) {
        this.deleteErrorMessage = 'SSN or ITIN cannot be deleted. Use edit to switch between SSN and ITIN.';
      } else if (this.entry === STATIC_DATA.PROVIDER && event.data.identifierCode === 'SSN' && !this.infoData.isProvidingServices) {
        this.notificationService.error(
          'Error',
          'Cannot delete SSN if Providing CDPAP Services is No'
        );
      } else {
        if (selectedData) {
          this.delete(selectedId);
        }
      }

    }

    if (event && event.action === 'unmask') {
      const selectedRowType = selectedData.identifierCode || selectedData.type;
      const payload = this.getUnMaskAPIPayload(selectedRowType);

      this.leadDetailsService.unmaskIdentifier(this.programCode, payload).subscribe(data => {
        const selectedgridItem = this.copyOfGridData.find(cgd => cgd.identifierCode === selectedRowType);
        // This guard prevents UI crashes when identifier labels differ across environments.
        if (!selectedgridItem) {
          return;
        }

        const raw = data.responsedata.replace(/\D/g, '');

        selectedgridItem.value =
          selectedRowType === 'SSN' || selectedRowType === 'TIN' || selectedRowType === 'ITIN'
            ? `${raw.slice(0, 3)}-${raw.slice(3, 5)}-${raw.slice(5, 9)}`
            : selectedRowType === 'EIN' || selectedRowType === 'EINES'
              ? `${raw.slice(0, 2)}-${raw.slice(2, 9)}`
              : data.responsedata;
      });
    }
  }

  getUnMaskAPIPayload(identifierType: string) {
    return {
      "programCode": this.programCode,
      "identifier": this.identifier.toString(),
      "source": 'PPL@Home',
      "identifierType": identifierType,
      "userType": this.type,
      "userCategory": this.category
    };
  }

  limitDigits(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 9) {
      value = value.slice(0, 9);
    }

    event.target.value = value;
    this.identifierForm.get('value')?.setValue(value);
  }

  toggleITIN(): void {
    this.itinVisible = !this.itinVisible;
    const raw = (this.identifierForm.get('value')?.value || '').replace(/\D/g, '');
    if (this.itinVisible && raw) {
      let formatted = raw;
      if (raw.length > 5) {
        formatted = raw.slice(0, 3) + '-' + raw.slice(3, 5) + '-' + raw.slice(5);
      } else if (raw.length > 3) {
        formatted = raw.slice(0, 3) + '-' + raw.slice(3);
      }
      this.identifierForm.get('value')?.setValue(formatted);
    } else if (!this.itinVisible && raw) {
      this.identifierForm.get('value')?.setValue(raw);
    }
  }

  limitItinDigits(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 9) {
      value = value.slice(0, 9);
    }

    if (this.itinVisible) {
      let formatted = '';
      if (value.length > 5) {
        formatted = value.slice(0, 3) + '-' + value.slice(3, 5) + '-' + value.slice(5);
      } else if (value.length > 3) {
        formatted = value.slice(0, 3) + '-' + value.slice(3);
      } else {
        formatted = value;
      }
      event.target.value = formatted;
      this.identifierForm.get('value')?.setValue(formatted);
    } else {
      event.target.value = value;
      this.identifierForm.get('value')?.setValue(value);
    }
  }

  add() {
    this.mode = 'add';
    this.duplicateIdentifierError = '';
    this.identifierForm.reset();
    this.selectedIdentifierValue = null;
    this.identifierForm.controls['leadId'].setValue(this.selectedLead);
  }

  save() {
    const pageData = this.identifierForm.value;
    this.duplicateIdentifierError = '';
    this.ssnError = false;
    this.cinError = false;
    this.einError = false;
    this.einesError = false;
    this.tinError = false;
    this.itinError = false;
    if (!pageData.value || !pageData.value.trim()) {
      this.validationErrors = { value: 'Please enter an identifier value.' };
      return;
    }
    if (pageData.type === 'SSN') {
      const ssn = pageData.value.replace(/-/g, '');
      const regex = /^\d{9}$/;
      const reservedSsn = ['078051120', '721074426', '219099999'];
      const ssnRegex =
        /^(?!666|000|9\d{2})(?!.*(\d)(?:\1){8})(?!123456789)\d{3}(?!00)\d{2}(?!0000)\d{4}$/;
      // Check if ssn has exactly 9 digits first
      if (!regex.test(ssn)) {
        this.ssnError = true;
      }
      // Validate the ssn against the main regex
      if (!ssnRegex.test(pageData.value)) {
        this.ssnError = true;
      }
      if (reservedSsn.includes(pageData.value)) {
        this.ssnError = true;
      }
    }
    if (pageData.type === 'CIN') {
      const cinRegex = /^\d{7}$/;
      if (!cinRegex.test(pageData.value)) {
        this.cinError = true;
      }
    }
    if (pageData.type === 'EIN') {
      const einRegex = /^(?:\d{2}-\d{7}|\d{9})$/;
      if (!einRegex.test(pageData.value)) {
        this.einError = true;
      }
    }
    if (pageData.type === 'EINES') {
      const einesDigits = pageData.value.replace(/\D/g, '');
      if (!/^\d{9}$/.test(einesDigits)) {
        this.einesError = true;
      }
    }
    if (pageData.type === 'TIN') {
      const tinRegex = /^\d{3}-?\d{2}-?\d{4}$/;
      if (!tinRegex.test(pageData.value)) {
        this.tinError = true;
      }
    }
    if (pageData.type === 'ITIN') {
      const itin = pageData.value.replace(/-/g, '');
      if (!/^\d{9}$/.test(itin)) {
        this.itinError = true;
      } else if (itin[0] !== '9') {
        this.itinError = true;
      } else {
        const middleDigits = parseInt(itin.substring(3, 5), 10);
        const validMiddleRanges =
          (middleDigits >= 50 && middleDigits <= 65) ||
          (middleDigits >= 70 && middleDigits <= 88) ||
          (middleDigits >= 90 && middleDigits <= 92) ||
          (middleDigits >= 94 && middleDigits <= 99);
        if (!validMiddleRanges) {
          this.itinError = true;
        }
      }
      if (/^(\d)\1{8}$/.test(itin)) {
        this.itinError = true;
      }
    }
    try {
      this.validationErrors = performValidation(
        this.identifierForm.value,
        this.secData,
        'Save', undefined, undefined, this.sectionService.getFlow(), this.sectionService.getEnrollmentType()
      );
      this.logger.debug('Error in validations', this.validationErrors);
    } catch {
      this.logger.debug('Error in validations');
    }
    if (Object.keys(this.validationErrors).length === 0 && !this.ssnError && !this.cinError && !this.einError && !this.einesError && !this.tinError && !this.itinError) {
      if (pageData.type === 'ITIN' && pageData.value) {
        pageData.value = pageData.value.replace(/-/g, '');
      }
      const payload = this.preparePayload(pageData);
      this.leadDetailsService
        .createUpdateLeadIdentifier(payload, this.entry)
        .subscribe({
          next: (response: any) => {
            // The gateway can relay the 406 duplicate body on the success channel.
            if (isDuplicateIdentifierPayload(response)) {
              this.setDuplicateIdentifierError(pageData.type);
              return;
            }
            this.logger.info('Lead Identifier saved successfully');
            this.leadUpdated.emit(true);
            this.cancelEdit();
          },
          error: (err: unknown) => {
            // Keep the form open on a duplicate so the entered value can be corrected.
            if (isDuplicateIdentifierError(err)) {
              this.setDuplicateIdentifierError(pageData.type);
              return;
            }
            this.leadUpdated.emit(true);
            this.cancelEdit();
          },
        });
    }
  }

  /** Builds the inline duplicate message, naming the identifier as the form labels it. */
  private setDuplicateIdentifierError(type: string) {
    const label =
      this.identifiersList.find((item) => item.lkpValueCode === type)?.lkpValueName?.trim() ||
      type ||
      'identifier';
    this.duplicateIdentifierError = `This ${label} is already on file for another record. Please verify the ${label}.`;
    this.logger.info('Duplicate identifier rejected by backend', type);
  }

  extractId(identifier: any) {
    let id = identifier.leadIdentifierId;
    if (this.entry === STATIC_DATA.REFERRAL) {
      id = identifier.referralIdentifierId;
    }
    if (this.entry === STATIC_DATA.PARTICIPANT_ENROLLMENT) {
      id = identifier.enrollmentIdentifierId;
    }
    if (this.entry === STATIC_DATA.PROVIDER_ENROLLMENT) {
      id = identifier.providerIdentifierId;
    }
    return id;
  }

  delete(identifierId: number) {
    /* let id = identifier?.leadIdentifierId;
    if (this.entry === STATIC_DATA.REFERRAL) {
      id = identifier.referralIdentifierId;
    }
    if(this.entry === STATIC_DATA.PARTICIPANT_ENROLLMENT){
      id = identifier.enrollmentIdentifierId
    } */

    //let id = this.extractId(identifier);

    this.leadDetailsService
      .deleteLeadIdentifier(identifierId, this.entry, this.programCode)
      .subscribe(() => {
        this.logger.info('Identified deleted successfully');
        this.leadUpdated.emit(true);
      });
  }

  preparePayload(
    source: LeadDetailIdentifiersModel
  ): LeadDetailIdentifiersAddUpdateModel {
    const { leadIdentifierId, ...identifierData } = source;
    const leadDetailIdentifierId =
      this.mode === 'edit' ? leadIdentifierId : null;

    if (identifierData.type === 'CIN') {
      identifierData.value = identifierData.value.toUpperCase();
    }
    const existingIdentifier = this.gridData
      ? this.gridData.find((row) => {
        if (
          row.leadIdentifierId === leadDetailIdentifierId ||
          row.referralIdentifierId === leadDetailIdentifierId ||
          row.enrollmentIdentifierId === leadDetailIdentifierId ||
          row.providerIdentifierId === leadDetailIdentifierId
        ) {
          return row;
        }
      })
      : null;

    return {
      id: this.selectedLead,
      identifierId: leadDetailIdentifierId,
      programCode: this.programCode,
      identifier: {
        ...(identifierData as LeadDetailIdentifiers),
        modifiedDate: existingIdentifier?.modifiedDate,
      },
    };
  }

  getAddedIdentifier(identifier: string): boolean {
    const created = this.createdIdentifiers.find(
      (identifier) => identifier.toLowerCase() === identifier.toLowerCase()
    );
    this.logger.info('Added identifier', this.createdIdentifiers, identifier);
    if (created) {
      return true;
    } else {
      return false;
    }
  }

  getTooltipTitle() {
    const selectedLkpValueName = this.identifiersList.find(
      (identifer) => this.selectedIdentifierValue === identifer.lkpValueCode
    )?.lkpValueName;

    return selectedLkpValueName ? `Please Enter ${selectedLkpValueName}` : '';
  }
}
